if(!customElements.get('product-card')) {
  /**
   * ProductCard custom element that represents a product card on the site
   * Handles rendering, interactions, and data management for product cards
   * @class
   * @extends {HTMLElement}
   */
  class ProductCard extends HTMLElement {
    /**
     * Creates an instance of ProductCard
     * Sets up initial properties and state
     * @constructor
     */
    constructor() {
      super();
      // disable gallery flag
      this.disableGallery = window.theme.utilities.stringToBoolean(this.dataset.disableGallery);
      // rendering mode
      this.renderingMode = this.dataset.renderingMode || 'server-side';
      this.showSoldOutState = window.theme.utilities.stringToBoolean(this.dataset.showSoldOutState);
      // default variant id
      this.defaultVariantId = parseInt(this.dataset.defaultVariantId, 10);
      // elements
      this.elements = {};
      this.templates = {};
      // atc on variant resolve flag
      this.atcOnVariantResolve = true;
      // promotion
      this.promotion = {};
      // variant
      this.variant = {};
      // slider
      this.slider = null;
      // selected swatch data
      this.selectedSwatchData = null;
      // selected swatch element
      this.selectedSwatchElement = null;
    }

    /**
     * Determines if lazy loading is enabled for this product card
     * @returns {boolean} Whether lazy loading is enabled
     */
    isLazyLoad(){
      // return true if the component has the lazy-load class
      return this.classList.contains("lazy-load");
    }

    /**
     * Determines if debugging is enabled for this product card
     * Returns true if the card has debug class or global debug is enabled
     * @returns {boolean} Whether debugging is enabled
     */
    allowDebug(){
      // return true if the component has the debug class
      return this.classList.contains("debug");
    }

    /**
     * Lifecycle callback when the element is added to the DOM
     * Either initializes the component or sets up lazy loading
     */
    async connectedCallback() {
      // lazy load flag
      if (this.isLazyLoad()) {
        // generate basic skeleton
        this.generateSkeleton();
        // observe the component for lazy loading
        window.theme.utilities.lazyLoadComponentObserver.observe(this);
      } else {
        // initialize the component
        await this.init();
      }
    }

    /**
     * Generates a basic skeleton for the product card
     */
    generateSkeleton(){
      // generate the skeleton
      this.skeleton = window.theme.utilities.createElementsFromHTML(`
        <div class="card-product-skeleton color-background-2">
          <div class="card-product-skeleton__media gradient">
            <div class="card-product-skeleton__media-image"></div>
          </div>
          <div class="card-product-skeleton__content">
            <div class="card-product-skeleton__title gradient"></div>
            <div class="card-product-skeleton__price gradient"></div>
          </div>
        </div>
      `);
      // insert the skeleton into the component
      this.appendChild(this.skeleton);
    }

    /**
     * Initializes the product card component
     * Sets up data, elements, event listeners, and renders content if needed
     */
    async init(){
      try {
        // allow debug flag
        if(this.allowDebug()) console.log('product-card.js: init()', this);
        // set product data
        this.product = await this.setProduct();
        if(this.allowDebug()) console.log('product-card.js product:', this.product);
        // set default variant
        this.variant = this.setDefaultVariant();
        if(this.allowDebug()) console.log('product-card.js variant:', this.variant);
        // set template elements HTML
        this.setTemplateHTML();
        // Check rendering mode and perform client-side rendering if needed
        if (this.renderingMode === 'client-side') {
          this.renderClientSide();
        }
        // set content element variables
        this.setContentElements();
        // set event listeners
        this.setEventListeners();
        // set default swatch data from the rendered card + variant
        this.setDefaultSwatchData();
        // generate the slider for the media, if we can figure out a clean way to do this we should really have the slider generate on mouseover.
        // for mobile, let's generate the slider.
        if(window.theme.utilities.isMobile()){
          this.generateSwatchMediaSlider(this.selectedSwatchData);
        }
        // inject quickshop
        this.injectQuickShop();
        // set observers
        this.setObservers();
        // add the initialized class
        this.classList.add('initialized');
        // remove the lazy-load class
        this.classList.remove('lazy-load');
        // log the initialization
        if(this.allowDebug()) console.log('product-card.js: init() finished.', this.product, this);
      } catch (error) {
        console.error('Error initializing component:', error, this);
      }
    }

    /**
     * Sets the default variant for the product card
     * Prioritizes default variant ID from the product data
     * @returns {Object|null} The default variant object or null if not found
     */
    setDefaultVariant(){
      // early exit conditions
      if(!this.product) {
        if(this.allowDebug()) console.error('product-card.js: setDefaultVariant() failed, no product found.', this);
        return null;
      }

      // check if the default variant id is set
      if(this.defaultVariantId) {
        // find the variant by id
        return this.findVariantById(this.defaultVariantId);
      } else if(this.product.is_parent_product){
        // check if the default child variant id is set
        return this.findVariantById(this.product.default_child_variant_id);
      } else if (this.product.variants?.length > 0) {
        // return the first variant if no default variant id is set
        return this.product.variants[0];
      } else {
        if(this.allowDebug()) console.error('product-card.js: setDefaultVariant() failed, no variant found.', this);
        return null;
      }
    }

    /**
     * Fetches and parses the product data from the JSON script tag
     * @returns {Object} The parsed product data
     */
    async setProduct(){
      // if we are not rendering client-side, return the product data from the JSON script tag
      // if we are rendering client-side, the product will be fetched from the url stored in the data-fetch-url attribute, this url will return a JSON string with the product data that must be parsed.
      if(this.renderingMode !== 'client-side') {
        return window.theme.utilities.parseConfig(this);
      } else {
        // return the product data
        return await this.fetchProduct();
      }
    }

    /**
     * Fetches and parses the product data from the JSON script tag
     * @returns {Object} The parsed product data
     */
    async fetchProduct(){
      return await fetch(this.dataset.fetchUrl).then(response => response.json());
    }

    /**
     * Finds and stores references to template elements
     * These templates are used for rendering parts of the component
     */
    setTemplateHTML(){
      // get the quick view template
      this.templates.quickViewTemplateHTML = `
        <div class="quick-shop-container">
          <div class="quick-add no-js-hidden small-hide">
            <modal-opener data-modal="#QuickAdd-${this.product.id}">
              <button
                id="${this.dataset.productFormId}-submit"
                type="submit"
                name="add"
                class="quick-add__submit"
                aria-haspopup="dialog"
                aria-labelledby="${this.dataset.productFormId}-submit title-${this.dataset.sectionId}-${this.product.id}"
                data-product-url="${this.product.url}"
              >
                ${window.theme.language_strings.product_quick_view}
                <div class="loading-overlay__spinner hidden">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    class="spinner"
                    viewBox="0 0 66 66"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </button>
            </modal-opener>
            <quick-add-modal id="QuickAdd-${this.product.id}" class="quick-add-modal">
              <span class="modal-overlay"></span>
              <div
                role="dialog"
                aria-label="${window.theme.language_strings.product_quick_view_view_details}"
                aria-modal="true"
                class="quick-add-modal__content global-settings-popup"
                tabindex="-1"
              >
                <button
                  id="ModalClose-${this.product.id}"
                  type="button"
                  class="quick-add-modal__toggle"
                  aria-label="${window.theme.language_strings.accessibility_close}"
                >
                  ${window.theme.elements.svg_close}
                </button>
                <div id="QuickAddInfo-${this.product.id}" class="quick-add-modal__content-info"></div>
              </div>
            </quick-add-modal>
          </div>
        </div>
      `;
      // get the quick add to bag template
      this.templates.quickAddToBagTemplateHTML = `
        <div class="quick-shop-container">
          <div class="quick-add no-js-hidden small-hide">
            <quick-add-to-bag
              class="quick-add-to-bag"
            >
              <button
                id="${this.dataset.productFormId}-trigger"
                type="button"
                class="quick-add-to-bag__trigger"
                data-product-url="${this.product.url}"
              >
                Add To Bag
                <div class="loading-overlay__spinner hidden">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    class="spinner"
                    viewBox="0 0 66 66"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </button>
              <div class="quick-add-to-bag__option-select-container"></div>
              <div class="quick-add-to-bag__cart-messages"></div>
            </quick-add-to-bag>
          </div>
        </div>
      `;
      // set the promotion template
      this.templates.promotionTemplateHTML = `
        <product-promotion
          class="card__promotion lazy-load"
          id="product-promotion-${this.product.id}"
        >
          <script type="application/json" id="product-promotion-${this.product.id}__json" component-config>
            {
              "template": "card",
              "fetch": {
                "url": "${this.variant.url}"
                ${this.product.is_parent_product ? `
                  ,"backup": {
                    "url": "${this.product.default_child_variant_url}"
                  }
                ` : ''}
              }
            }
          </script>
          <div class="product__promotion"></div>
        </product-promotion>
      `;
      // set the component base template
      this.templates.componentTemplateHTML = `
        <div class="card-wrapper product-card-wrapper">
          <div class="card card--standard card--media">
            <div class="card__inner color-background-2 gradient ratio" style="--ratio-percent: calc((4/3) * 100%);">
              <div class="card__media"></div>
            </div>
            <div class="card__content">
              <div class="card__information"></div>
              ${this.product.block_promotions ? '' : this.templates.promotionTemplateHTML}
            </div>
          </div>
        </div>
      `;
    }

    /**
     * Finds and stores references to content elements
     * These elements contain the visible content of the component
     */
    setContentElements(){
      // get the price element
      this.elements.priceElement = this.querySelector('.card-price-container');
      // get the product promotion
      this.elements.productPromotion = this.querySelector('product-promotion');
      // get the product swatches
      this.elements.productSwatches = this.querySelector('.product-card-swatches__container');
    }

    /**
     * Renders the product card content on the client side
     * Used when client-side rendering mode is enabled
     */
    renderClientSide() {
      if(this.allowDebug()) console.log('product-card.js: renderClientSide()', this.product);
      // early exit conditions
      if (!this.product || !this.templates.componentTemplateHTML) {
        console.error('Missing data or client template for client-side rendering', this);
        return;
      }
      // try to render the client side
      try {
        // clear the existing content
        this.replaceChildren();
        // clone the template content into a new element
        const templateHTML = window.theme.utilities.createElementsFromHTML(this.templates.componentTemplateHTML);
        // render the card media
        this.renderClientSideCardMedia(templateHTML);
        // render the card media badges
        this.renderClientSideCardMediaBadges(templateHTML);
        // render the card info badges
        this.renderClientSideCardInfoBadges(templateHTML);
        // render the card information
        this.renderClientSideCardTitle(templateHTML);
        // render the card price
        this.renderClientSideCardPrice(templateHTML);
        // render the card swatches
        this.renderClientSideCardSwatches(templateHTML);
        // insert the rendered template into the component
        this.appendChild(templateHTML);
        if(this.allowDebug()) console.log('product-card.js: renderClientSide() finished', templateHTML);
      } catch (error) {
        console.error('Error rendering client-side:', error, this);
      }
    }

    /**
     * Renders the media overlaybadges portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardMediaBadges(container){
      // get the badges container
      const mediaBadgesContainer = container.querySelector('.card__inner');
      // early exit conditions
      if(!mediaBadgesContainer) return;
      // render the badges
      // if the product is a membership product, render the membership messaging
      const productMembership = this.product.membership || null;
      const productMembershipRequired = productMembership?.membership_required || false;
      const productMembershipGroup = productMembership?.membership_group_required || null;
      const productMembershipGroupHandle = productMembershipRequired ? String(window.theme.utilities.handleize(productMembershipGroup?.title) || 'default').replace('-', '_') : '';
      if(productMembershipGroupHandle.length > 0){
        const membershipBadgeTemplateHTML = `
          <div class="members-messaging">
            <div class="members-messaging--icon">
              ${window.theme.elements.membership_icons[productMembershipGroupHandle]}
            </div>
            </div>
          `;
        mediaBadgesContainer.appendChild(window.theme.utilities.createElementsFromHTML(membershipBadgeTemplateHTML));
      }
      /**
       * Generate badge html based on the product tags, where the tag is formatted as "imageLabel:text|background-color|text-color"
       * Tags is a general string list, not all tags follow this format. We only evaluate the tags that follow this format.
       * If we are to show the sold out state, we will clear the badge html and add the sold out state badge.
       * Only append the badge html if there are badges to show.
       */
      let badgeHTML = '';
      if(!this.showSoldOutState) {
        this.product.tags.forEach((tag) => {
          if(tag.includes('imageLabel:')){
            const badgeArray = tag.split(':')[1].split('|');
            const badgeText = badgeArray[0];
            const badgeBackgroundColor = badgeArray[1];
            const badgeTextColor = badgeArray[2];
            // do not show "new" badges if we are on a collection handle containing "new-arrival"
            let renderBadge = true;
            if(badgeText.toLowerCase() == "new" && window.location.pathname.includes('new-arrival')){
              renderBadge = false;
            }
            if(renderBadge) badgeHTML += `<span class="badge badge-new" style="background-color: ${badgeBackgroundColor}; color: ${badgeTextColor}">${badgeText}</span>`;
          }
        });
      } else {
        badgeHTML = `<span class="badge badge-new" style="background-color: ${window.theme.colors.sold_out_badge_background_color}; color: ${window.theme.colors.sold_out_badge_text_color}">${window.theme.language_strings.variant_sold_out}</span>`;
      }
      if(badgeHTML.length > 0){
        badgeHTML = `<div class="image__badges">${badgeHTML}</div>`;
        mediaBadgesContainer.appendChild(window.theme.utilities.createElementsFromHTML(badgeHTML));
      }
    }

    /**
     * Renders the media (images) portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardMedia(container) {
      // get the media container
      const mediaContainer = container.querySelector('.card__media');
      // early exit conditions
      if (!mediaContainer) return;
      if(this.allowDebug()) console.log('product-card.js: renderClientSideCardMedia()', this.product);
      // Get media from data
      // primary product card media is stored in the swatch array
      // search for a swatch where the color name matches the variant color
      // the swatch's media array is a string array of image tag HTML
      let media = this.product.swatches.find((swatch) => swatch.color == this.variant.color)?.media || [];
      // if we cannot find swatch media, use the product media
      if(!media || media.length === 0){
        media = this.product.media;
      }
      if (media && media.length > 0) {
        // clear the media container
        mediaContainer.replaceChildren();
        const firstMedia = media[0].replace('loading="lazy"', '');
        let mediaContainerHTML = `
          <a href="${this.variant.url || '#'}" class="media media--transparent ${media.length > 1 ? 'media--hover-effect' : ''}">
            ${firstMedia}
            ${media.length > 1 ? media[1] : ''}
          </a>
        `;
        // insert the link node into the media container
        mediaContainer.appendChild(window.theme.utilities.createElementsFromHTML(mediaContainerHTML));
      }
    }

    /**
     * Renders the product info badges portion of the card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardInfoBadges(container){
      // get the info container
      const infoBadgesContainer = container.querySelector('.card__information');
      // early exit conditions
      if (!infoBadgesContainer) return;
      /**
       * Generate badge html based on the product tags, where the tag is formatted as "badge:text|background-color|text-color"
       * Tags is a general string list, not all tags follow this format. We only evaluate the tags that follow this format.
       * If we are to show the sold out state, we will clear the badge html and add the sold out state badge.
       * Only append the badge html if there are badges to show.
       */
      let darkModeBadgeHTML = '';
      this.product.tags.forEach((tag) => {
        if(tag.includes('darkBadge:')){
          const badgeArray = tag.split(':')[1].split('|');
          const badgeText = badgeArray[0];
          const badgeBackgroundColor = badgeArray[1];
          const badgeTextColor = badgeArray[2];
          darkModeBadgeHTML += `<span class="badge badge-new" style="background-color: ${badgeBackgroundColor}; color: ${badgeTextColor}">${badgeText}</span>`;
        }
      });
      if(darkModeBadgeHTML.length > 0){
        darkModeBadgeHTML = `<div class="card__badges card__badges-dark">${darkModeBadgeHTML}</div>`;
        infoBadgesContainer.appendChild(window.theme.utilities.createElementsFromHTML(darkModeBadgeHTML));
      }
      /**
       * Generate badge html based on the product tags, where the tag is formatted as "badge:text|background-color|text-color"
       * Tags is a general string list, not all tags follow this format. We only evaluate the tags that follow this format.
       * If we are to show the sold out state, we will clear the badge html and add the sold out state badge.
       * Only append the badge html if there are badges to show.
       */
      let badgeHTML = '';
      this.product.tags.forEach((tag) => {
        if(tag.includes('badge:')){
          const badgeArray = tag.split(':')[1].split('|');
          const badgeText = badgeArray[0];
          const badgeBackgroundColor = badgeArray[1];
          const badgeTextColor = badgeArray[2];
          badgeHTML += `<span class="badge badge-new" style="background-color: ${badgeBackgroundColor}; color: ${badgeTextColor}">${badgeText}</span>`;
        }
      });
      if(badgeHTML.length > 0){
        badgeHTML = `<div class="card__badges">${badgeHTML}</div>`;
        infoBadgesContainer.appendChild(window.theme.utilities.createElementsFromHTML(badgeHTML));
      }
    }
    /**
     * Renders the product title portion of the card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardTitle(container) {
      // get the info container
      const cardTitleContainer = container.querySelector('.card__information');
      // early exit conditions
      if (!cardTitleContainer) return;

      const titleHTML = `
        <h3 class="card__heading h5">
          <a href="${this.variant.url || '#'}" class="full-unstyled-link">
            ${this.product.title_html || this.product.title || 'Product'}
          </a>
        </h3>
      `;
      // insert the title into the info container
      cardTitleContainer.appendChild(window.theme.utilities.createElementsFromHTML(titleHTML));
      // product subtitle, if applicable
      const productSubtitle = this.product.subtitle;
      if(productSubtitle){
        const subtitleHTML = `
          <p class="${this.dataset.productSubtitleClass}">${productSubtitle}</p>
        `;
        cardTitleContainer.appendChild(window.theme.utilities.createElementsFromHTML(subtitleHTML));
      }
    }

    /**
     * Renders the price portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardPrice(container) {
      // get the info container, price element is a child of the info container
      const priceContainer = container.querySelector('.card__information');
      // early exit conditions
      if (!priceContainer) return;
      const priceHTML = this.showSoldOutState ? `<span class="price--sold-out">${window.theme.language_strings.variant_sold_out}</span>` : this.variant.price_html || this.product.price_range_html;
      const priceContainerHTML = `
        <div class="card-information">
          <div class="card-price-container">
            ${priceHTML}
          </div>
        </div>
      `;
      priceContainer.appendChild(window.theme.utilities.createElementsFromHTML(priceContainerHTML));
    }

    /**
     * Renders the color swatches portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardSwatches(container) {
      // get the swatches container
      const cardContent = container.querySelector('.card__content');
      // early exit conditions
      if (!cardContent || !this.product.swatches || !this.product.swatches.length) return;

      // maximum number of swatches to display
      const maxSwatches = 3;

      // Find current variant's swatch
      const currentVariantColor = this.variant.color;
      const currentVariantSwatch = this.product.swatches.find(swatch =>
        swatch.color === currentVariantColor && this.product.available_colors.includes(swatch.color)
      );

      // Create a prioritized list of swatches
      // 1. Start with the current variant's swatch
      // 2. Add other available swatches until we reach maxSwatches
      let swatchesToRender = [];

      // Add current variant swatch first, if it exists
      if (currentVariantSwatch) {
        swatchesToRender.push(currentVariantSwatch);
      }

      // Add other available swatches until we reach maxSwatches
      if (this.product.swatches.length > 1) {
        this.product.swatches.forEach(swatch => {
          // Only add different swatches that are available and not already included
          if (swatch.color !== currentVariantSwatch.color && this.product.available_colors.includes(swatch.color) && swatchesToRender.length < maxSwatches) {
            swatchesToRender.push(swatch);
          }
        });
      }

      // Only continue if we have swatches to render
      if (swatchesToRender.length === 0) return;

      // start the swatches container html
      let swatchesContainerHTML = `
        <div class="product-card-swatches__container">
          <ul
            class="product-card-swatches product-card__option product-card__options--${window.theme.utilities.handleize(this.product.color_option_name)}"
            data-option="${this.product.color_option_name}"
            data-option-key="${this.product.color_option_key}"
          >
      `;

      // Render each swatch in our prioritized order
      swatchesToRender.forEach((swatch, index) => {
        swatchesContainerHTML += `
          <li>
            <div class="product-card-swatch swatch--${swatch.handle}">
              <input
                type="radio"
                id="product-${this.product.id}-color-${swatch.handle}"
                name="color[${this.product.id}]"
                value="${swatch.color}"
                class="product__option-input product__option-swatch"
                data-color="${swatch.handle}"
                data-option-key="${this.product.color_option_key}"
                ${index === 0 ? 'checked' : ''}
              >
              <label
                for="product-${this.product.id}-color-${swatch.handle}"
                class="product__option-label"
              >
                <span class="visually-hidden">${swatch.color}</span>
              </label>
            </div>
          </li>
        `;
      });

      // Add "more colors" link if we have more swatches than the max
      if (this.product.available_colors.length > maxSwatches) {
        swatchesContainerHTML += `
          <li class="product-card-swatches-link-more">
            <a href="${this.variant.url}" class="link full-unstyled-link">${window.theme.language_strings.product_swatches_more_colors}</a>
          </li>
        `;
      }

      // close the swatches container
      swatchesContainerHTML += `
          </ul>
        </div>
      `;

      // insert the swatches container into the container
      cardContent.appendChild(window.theme.utilities.createElementsFromHTML(swatchesContainerHTML));

      // Log information if debug is enabled
      if (this.allowDebug()) {
        console.log('product-card.js: renderClientSideCardSwatches(), Rendered swatches:', swatchesToRender.map(s => s.color), this);
        console.log('product-card.js: renderClientSideCardSwatches(), Current variant color:', currentVariantColor, this);
      }
    }

    /**
     * Sets up event listeners for the product card
     * Uses event delegation for efficiency
     */
    setEventListeners(){
      // event delegation via clicks

      this.addEventListener('click', (event) => {
        // swatch click
        const { target } = event;
        // find the grid item
        const gridItem = target.closest("li.grid__item");
        // remove the nudge class if present
        if (gridItem?.classList.contains("nudge")) gridItem.classList.remove("nudge");
        // handle swatch click
        if(this.hasSwatches()){
          if (event.target && event.target.matches("input.product__option-input.product__option-swatch")) {
            this.handleProductSwatchClick(event.target);
          }
        }
        // option click
        if (event.target && event.target.matches("input.product__option-input")) {
          this.handleProductOptionClick(event.target);
        }
        // add to cart click
        if (event.target && event.target.matches("button.product-card__add-to-bag")) {
          this.handleAddToCartClick(event.target);
        }
      });
      // handle swatch change
      this.addEventListener('product-card:swatch-change', this.handleSwatchChange);
      // handle promotion change
      this.addEventListener('product-promotion:change', this.handlePromotionChange);
      // handle mouseover on card_inner
      const cardInner = this.querySelector('.card__inner');
      if(cardInner){
        cardInner.addEventListener('mouseover', (event) => {
          if(!this.slider){
            this.generateSwatchMediaSlider(this.selectedSwatchData);
          }
        });
      }
    }

    /**
     * Sets up mutation observers to watch for changes to the component
     * Particularly watches for class changes that affect quick shop functionality
     */
    setObservers() {
      // Track the initial class state for comparison.
      let previousClassList = [...this.classList];
      // class change observer
      const classChangeObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === "attributes" && mutation.attributeName === "class") {
            const currentClassList = [...this.classList];
            const addedClasses = currentClassList.filter(cls => !previousClassList.includes(cls));
            const removedClasses = previousClassList.filter(cls => !currentClassList.includes(cls));

            // Determine if specific classes that trigger quick shop changes have been modified.
            const quickShopChange = [...addedClasses, ...removedClasses].some(cls => cls === "with__quickview" || cls === "with__add-to-bag");

            // React to changes related to quickshop functionality.
            if (quickShopChange) {
              if(this.allowDebug()) console.log('product-card.js: classChangeObserver(), a change in quickshop classes has been detected.', this);
              this.injectQuickShop();
            }

            if(addedClasses.includes("debug")){
              if(this.allowDebug()) console.log('product-card.js: classChangeObserver(), debug class has been added for the card.', this);
            }

            // Update the class list reference.
            previousClassList = currentClassList;
          }
        });
      });

      classChangeObserver.observe(this, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    /**
     * Injects the appropriate quick shop interface based on component classes
     * Either quick view or quick add to bag functionality
     */
    injectQuickShop() {
      // early exit conditions
      if(this.product.is_bundle_product) return;
      // quick shop container is a child of .card__media
      const cardMedia = this.querySelector('.card__media');
      if(!cardMedia) return;

      // Check for presence of quickshop classes.
      const hasQuickView = this.classList.contains("with__quickview");
      const hasAddToBag = this.classList.contains("with__add-to-bag");

      // Just in case, clear the quickshop container.
      // search for .quick-shop-container and remove it
      const quickShopContainer = this.querySelector('.quick-shop-container');
      if(quickShopContainer){
        quickShopContainer.remove();
      }

      // Inject quickshop action code on the present classes.
      try {
        // inject the quick view
        if (hasQuickView) {
          const content = window.theme.utilities.createElementsFromHTML(this.templates.quickViewTemplateHTML);
          // insert the new quick shop container into .card__media
          cardMedia.appendChild(content);
        } else if (hasAddToBag) {
          // inject the quick add to bag
          const content = window.theme.utilities.createElementsFromHTML(this.templates.quickAddToBagTemplateHTML);
          // insert the new quick shop container into .card__media
          cardMedia.appendChild(content);
        }
      } catch (error) {
        console.error("Error injecting quick shop content:", error, this);
      }
    }

    /**
     * Sets default swatch data when there's only one swatch option
     * Simulates a user click on the first swatch
     */
    setDefaultSwatchData() {
      if(this.allowDebug()) console.log('product-card.js: setDefaultSwatchData()', this);
      // if we do not have a selected swatch, get the first swatch
      let selectedSwatch = this.querySelector(".product__option-swatch:checked");
      if(!selectedSwatch){
        selectedSwatch = this.elements.productSwatches?.querySelector(".product__option-swatch:first-of-type");
      }
      // set the selected swatch data, if the element is null, the function sets empty values.
      this.setSelectedSwatchData(selectedSwatch);
    }

    /**
     * Handles promotion change events from the promotion component
     * @param {CustomEvent} event - The promotion change event
     */
    handlePromotionChange(event){
      // early exit conditions
      if (this.contains(event.detail.triggeringElement)) {
        this.promotion = event.detail.promotion;
      }
    }

    /**
     * Updates promotion variables when the variant changes
     * @param {Object} variant - The product variant object
     */
    async updatePromotionVariables(variant) {
      if (!variant || !this.elements.productPromotion || this.elements.productPromotion.lazyLoad) return;

      const promotionFetchData = {
        url: variant.url
      };

      if (this.product.is_parent_product) {
        promotionFetchData.url = variant.parent_variant_url;
        promotionFetchData.backup = {
          url: variant.child_variant_url
        };
      }

      await this.elements.productPromotion.updateFetchData(promotionFetchData);
    }

    /**
     * Handles product swatch click events
     * Updates the selected swatch data and dispatches a swatch change event
     * @param {HTMLElement} swatch - The swatch input element that was clicked
     */
    handleProductSwatchClick(swatchElement){
      // early exit conditions
      if(!swatchElement) return;
      // set the selected swatch dataset
      this.setSelectedSwatchData(swatchElement);
      // dispatch swatch change event
      this.dispatchEvent(new CustomEvent('product-card:swatch-change', {
        bubbles: true,
        detail: { triggeringElement: this, swatchData: this.selectedSwatchData, swatchElement: this.selectedSwatchElement }
      }));
    }

    /**
     * Sets the selected swatch data and element
     * @param {Object} swatch - The swatch data object
     */
    setSelectedSwatchData(swatchElement){
      // Find the matching swatch object based on data-color attribute
      const handleizedColor = swatchElement?.dataset?.color || '';
      // find the matching swatch
      let matchingSwatch = this.product?.swatches?.find((swatch) => swatch.handle === handleizedColor);
      // exit if no matching swatch found
      if (!matchingSwatch) {
        if(this.allowDebug()) console.log('product-card.js: setSelectedSwatchData(), no matching swatch found', swatchElement);
        // reset the swatch data
        matchingSwatch = {};
        swatchElement = null
      }
      // set the selected swatch dataset
      this.selectedSwatchData = matchingSwatch;
      // set the selected swatch element
      this.selectedSwatchElement = swatchElement;
    }

    /**
     * Updates the product images based on the selected swatch
     * Creates a slider if there are multiple images and it's not disabled
     * @param {Object} swatch - The swatch data object
     */
    setProductImagesFromSwatch(swatch){
      if(this.allowDebug()) console.log('product-card.js: setProductImagesFromSwatch(), setting product images from swatch', swatch);
      // early exit conditions
      if(!swatch) return;
      // get the media wrapper
      const cardMediaContainer = this.querySelector(".card__media");
      if(!cardMediaContainer) return;
      // if we have a slider, destroy it.
      this.destroySwatchMediaSlider();
      // clear the main media wrapper
      cardMediaContainer.replaceChildren();
      // create the card media link
      const cardMediaLink = window.theme.utilities.createElementsFromHTML(`
        <a
          href="${swatch.url}"
          id="CardLink-${this.dataset.sectionId}-${this.product.id}"
          class="media media--transparent"
          aria-labelledby="CardLink-${this.dataset.sectionId}-${this.product.id} Badge-${this.dataset.sectionId}-${this.product.id}"
        ></a>
      `);
      cardMediaContainer.appendChild(cardMediaLink);

      // get the swatch images
      // swatches images is an array of HTML image tag strings.
      const swatchImages = swatch.media;

      if (swatchImages.length > 0) {
        // First, always populate the initial media container with 1-2 images
        const firstImageHTML = swatchImages[0];
        cardMediaLink.appendChild(window.theme.utilities.createElementsFromHTML(firstImageHTML));

        // Add second image if available and secondary images are enabled,
        // and the product has more than 2 images. If the product has more than 2 images,
        // the slider will be shown instead of the secondary image.
        if(swatchImages.length === 2){
          cardMediaLink.classList.add('media--hover-effect');
          cardMediaLink.appendChild(window.theme.utilities.createElementsFromHTML(swatchImages[1]));
        }
        // the slider is a secondary sibling element of the card media link. This element is only present if the product has more than 2 images, and the user is mouseovering the product card.
        this.generateSwatchMediaSlider(swatch);
      } else {
        // Fallback to default product media if no swatch images
        // add the first image to the card media link, if we are not lazy loading the component eager load the first image.
        const defaultImageHTML = this.product.media[0];
        // append the default image to the card media link
        cardMediaLink.appendChild(window.theme.utilities.createElementsFromHTML(defaultImageHTML));
      }
    }

    generateSwatchMediaSlider(swatch = null){
      if(this.allowDebug()) console.log('product-card.js: generateSwatchMediaSlider()', this);
      if(this.disableGallery) return;
      if(!swatch) return;
      // get the card media container
      const cardMediaContainer = this.querySelector(".card__media");
      if(!cardMediaContainer) return;
      // get the swatch images
      const swatchImages = swatch.media;
      if(!swatchImages) return;
      // Check if we need the slider
      const isMobile = window.theme.utilities.isMobile();
      let enableSlider = isMobile ? swatchImages.length > 1 : swatchImages.length > 2;
      if(!enableSlider) return;
      const initialIndex = isMobile ? 0 : 1;
      // Create carousel HTML with template literals
      let sliderHTML = `<div class="plp-card-carousel gradient">`;
      // Add carousel cells (limited to first 5)
      swatchImages.slice(0, 5).forEach(imageTag => {
        const imageTagHTML = imageTag.replace('src=', 'data-flickity-lazyload-src=').replace('srcset=', 'data-flickity-lazyload-srcset=').replace('loading="lazy"', '');
        sliderHTML += `<a class="carousel-cell media" href="${swatch.url}">${imageTagHTML}</a>`;
      });
      sliderHTML += `</div>`;
      // Append the slider to the media wrapper (as a sibling to the initial media container)
      const sliderWrapper = window.theme.utilities.createElementsFromHTML(sliderHTML);
      // insert the slider into the card media container
      cardMediaContainer.appendChild(sliderWrapper);
      // Initialize Flickity
      this.slider = new Flickity(sliderWrapper, {
        cellAlign: "right",
        contain: true,
        initialIndex: initialIndex,
        lazyLoad: true,
        wrapAround: true,
        pageDots: true
      });
      // Add slider classes based on mobile status
      if (isMobile && swatchImages.length > 1) {
        sliderWrapper.classList.add('first-slider');
      }
      // Add custom pagination if needed
      if (isMobile && swatchImages.length >= 4) {
        const flickityDots = sliderWrapper.querySelector('.flickity-page-dots');
        if (!flickityDots) return;
        flickityDots.classList.add('custom-pagination');
        // Create the dots function
        const createDots = (index = 0) => {
          // Clear existing dots
          flickityDots.replaceChildren();
          // Get slider configuration details
          const isFourSlides = swatchImages.length === 4;
          const dotsCount = index < 3 ? 3 : isFourSlides ? 1 : 2;
          const activeIndex = index < 3 ? index : isFourSlides ? 0 : index - 3;
          const barOnLeft = index >= 3;
          // Create and add pagination elements
          const dots = Array.from({ length: dotsCount }, (_, i) => {
            const dot = document.createElement('div');
            dot.classList.add('pagination-dot');
            if (i === activeIndex) dot.classList.add('active');
            return dot;
          });
          // Clear and add new dots
          flickityDots.replaceChildren(...dots);
          // Create and position bar
          const bar = document.createElement('div');
          bar.classList.add('pagination-bar');
          // Insert bar at the appropriate position
          if (barOnLeft) {
            flickityDots.insertBefore(bar, flickityDots.firstChild);
          } else {
            flickityDots.appendChild(bar);
          }
        };
        // Set up slide change event and initialize dots
        this.slider.on('change', createDots);
        createDots(0);
      }
    }

    destroySwatchMediaSlider(){
      if(this.allowDebug()) console.log('product-card.js: destroySwatchMediaSlider()', this);
      if(!this.slider) return;
      // get the card media container
      const cardMediaContainer = this.querySelector(".card__media");
      if(!cardMediaContainer) return;
      // destroy the slider
      this.slider.destroy();
      this.slider = null;
      // remove the slider wrapper
      const sliderWrapper = cardMediaContainer.querySelector('.plp-card-carousel');
      if(sliderWrapper){
        cardMediaContainer.removeChild(sliderWrapper);
      }
    }

    /**
     * Handles swatch change events
     * Updates images, price, and variant data based on the new swatch
     * @param {CustomEvent} event - The swatch change event
     * @returns {Promise<void>}
     */
    async handleSwatchChange(event){
      // update card urls
      this.setProductLinks(`${this.selectedSwatchData.url}`);

      // update media
      this.setProductImagesFromSwatch(this.selectedSwatchData);

      // update price element to the swatch price
      if(!this.showSoldOutState) {
        // update the price element
        this.setPriceElement(window.theme.utilities.createElementsFromHTML(this.selectedSwatchData?.price_html));
      }

      // update variant based on the swatch, use this to update promotions.
      const variantFromSwatch = this.findVariantById(this.selectedSwatchData.variant);
      if(variantFromSwatch){
        // update the variant
        this.variant = variantFromSwatch;
        // update promotion variables
        await this.updatePromotionVariables(this.variant);
      }

      // handle the quick add to bag
      const quickAddToBag = this.querySelector('quick-add-to-bag');
      if(quickAddToBag){
        if(quickAddToBag.isActive()) {
          // inject the product options
          await quickAddToBag.injectProductOptions();
        } else {
          // open the quick add to bag
          quickAddToBag.open();
        }
      }
    }

    /**
     * Handles non-swatch product option clicks
     * Updates selected options and finds matching variant
     * @param {HTMLElement} option - The option input element that was clicked
     * @returns {Promise<void>}
     */
    async handleProductOptionClick(option){
      // early exit conditions
      if(!option) {
        if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), no option', option);
        return;
      }
      if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), option', option);
      // get the quick add to bag
      const quickAddToBag = this.querySelector('quick-add-to-bag');
      // exit if no quick add to bag or the quick add to bag is not active
      if(!quickAddToBag || (quickAddToBag && !quickAddToBag.isActive())) {
        if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), no quick add to bag or the quick add to bag is not active', quickAddToBag);
        return;
      }
      // clear the cart message
      quickAddToBag.clearCartMessage();
      // update the product options availability
      quickAddToBag.updateProductOptionsAvailability();
      // get the quick add to bag options
      const quickAddToBagOptions = quickAddToBag.querySelectorAll('.product-card__option');
      // exit if the quick add to bag options length is 0
      // If the quick add to bag shows is NOT rendering options, move on, that is a special view with it's own handlers.
      if(quickAddToBagOptions.length === 0) {
        if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), no quick add to bag options', quickAddToBagOptions);
        return;
      }
      // At this point we have an active quick add to bag UI open, with options to select.
      const optionGroups = [...this.querySelectorAll('.product-card__option')];
      // get the selected options
      const selectedOptions = [...this.querySelectorAll('.product__option-input:checked')];
      // get the option groups count
      const optionGroupsCount = optionGroups.length;
      // get the selected options count
      const selectedOptionsCount = selectedOptions.length;
      // exit if the option groups count is not equal to the selected options count
      if(optionGroupsCount === selectedOptionsCount){
        /*
          When all options are selected, find a variant made up of the selected options in this.product.variants.
          Every entry in selectedOptions is an input:checked element.
          These input elements have data-option-key attribute which is a value like "option1" "option2" "option3" etc.
          These input elements also have a value attribute, which is a value that represents the value of "option1" "option2" etc of a variant in this.product.variants
          Find a variant in this.product.variants where the option keys match the related option value.
          Update product card UI with select variant information.
        */
        if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), all option groups have a value', optionGroups, selectedOptions);
        const selectedVariant = this.findMatchingVariant(selectedOptions);
        // exit if no selected variant
        if (!selectedVariant) {
          if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), no selected variant', selectedVariant);
          return;
        }
        // update the variant
        this.variant = selectedVariant;
        // update promotion variables
        await this.updatePromotionVariables(this.variant);
        // update product card links and price elements
        this.updateVariantUI(this.variant);
        // per client's request, when we have a resolved variant, add it to cart.
        if(this.atcOnVariantResolve){
          await this.handleAddToCart();
        }
      } else {
        if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), not all option groups have a value', optionGroups, selectedOptions);
        // Not all option groups have a value, do nothing at this point.
        return;
      }
    }

    /**
     * Finds a variant by its ID
     * @param {string|number} id - The variant ID to find
     * @returns {Object|null} The matching variant or null if not found
     */
    findVariantById(id) {
      // early exit conditions
      if(!id) return;
      // exit if no product variants
      if(!this.product.variants) return;
      // return the variant
      return this.product.variants.find((variant) => variant.id === id);
    }

    /**
     * Finds a variant that matches the selected options
     * @param {Object} selectedOptions - Map of option keys to selected values
     * @returns {Object|null} The matching variant or null if not found
     */
    findMatchingVariant(selectedOptions) {
      // early exit conditions
      if(!selectedOptions) return;
      // return the variant
      return this.product.variants.find((variant) => {
        return selectedOptions.every((option) => {
          const optionIndex = option.dataset.optionKey;
          const optionValue = option.value;
          return variant[optionIndex] === optionValue;
        });
      });
    }

    /**
     * Updates the UI elements based on the selected variant
     * @param {Object} variant - The selected variant
     */
    updateVariantUI(variant){
      // early exit conditions
      if(!variant) return;
      // update the product links
      this.setProductLinks(variant.url);
      // update the price element
      if(!this.showSoldOutState) {
        this.setPriceElement(window.theme.utilities.createElementsFromHTML(variant.price_html));
      }
    }

    /**
     * Handles add to cart button clicks
     * @param {HTMLElement} button - The clicked button
     * @returns {Promise<void>}
     */
    async handleAddToCartClick(button){
      // early exit conditions
      if(!button) return;
      // get the parent quick add to bag
      const parentQuickAddToBag = button.closest('quick-add-to-bag');
      // exit if no parent quick add to bag
      if(!parentQuickAddToBag) return;
      // exit if the card has swatches
      if(this.hasSwatches()) return;
      // get the selected swatch data
      const selectedSwatchData = this.getSelectedSwatchData();
      // exit if no selected swatch data
      if(!selectedSwatchData) return;
      // get the full variant data based off the swatch ID
      this.variant = this.findVariantById(this.selectedSwatchData.variant);
      // exit if no variant
      if(!this.variant) return;
      // handle add to cart
      await this.handleAddToCart();
    }

    /**
     * Adds the current product variant to the cart
     * Handles different product types (standard, parent, bundle, etc.)
     * @returns {Promise<void>}
     */
    async handleAddToCart(){
      // early exit conditions
      if(!Object.keys(this.variant).length > 0) return;
      try{
        // get the atc buttons
        const atcButtons = this.querySelectorAll('.product-card__add-to-bag');
        // set the atc buttons to loading
        atcButtons?.forEach((atcButton)=>{
          atcButton.setAttribute('aria-disabled', true);
            atcButton.classList.add('loading');
          atcButton.querySelector('.loading-overlay__spinner').classList.remove('hidden');
        });

        // compile the item data being sent to cart.
        let properties = {};

        // set the order type
        properties["_ord_type"] = this.product.order_type || "WHP";

        // Collect promotion properties
        if (Object.keys(this.promotion).length > 0) {
          // get the promotion properties
          const promotionProperties = window.theme.utilities.getPromotionProperties(this.promotion);
          // evaluate further if promotion properties are available
          if (Object.keys(promotionProperties).length > 0) {
            // Go through the promotionProperties return and add each property entry to the properties object
            Object.entries(promotionProperties).forEach(([key, value]) => {
              properties[`${key}`] = value.value;
            });
          }
        }

        // find other properties by looking for hiddein properties inputs.
        this.querySelectorAll(`input[type="hidden"][name^="properties["]`).forEach(el => {
          const propertyName = el.name.match(/\[([^)]+)\]/)?.[1];
          if (propertyName) properties[propertyName] = el.value;
        });

        // set the action
        const action = `${window.theme.routes.cart_add_url}.js`;
        // set the request body
        const requestBody = {
          items: [
            {
              id: this.variant.id,
              quantity: 1,
              properties: properties
            }
          ]
        };
        // fetch the action
        await fetch(action, {
          method: 'POST',
          body: JSON.stringify(requestBody),
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          }})
          .then((response) => response.json())
          .then((response) => {
            // exit if the response does not have items
            if(!response.hasOwnProperty('items')){
              // update atc buttons
              if(atcButtons){
                atcButtons.forEach((atcButton)=>{
                  atcButton.removeAttribute('aria-disabled');
                  atcButton.classList.remove('loading');
                  atcButton.querySelector('.loading-overlay__spinner').classList.add('hidden');
                });
              }
              // get the active quick add to bag
              const activeQuickAddToBag = this.querySelector('quick-add-to-bag.active');
              // set the cart message
              if(activeQuickAddToBag) activeQuickAddToBag.setCartMessage(response.description);
              return;
            }

            const cartDrawer = document.querySelector('#shopify-section-cart-drawer cart-drawer');
            if(cartDrawer){
              // refresh the cart drawer
              cartDrawer.refresh();
              // open the cart drawer
              cartDrawer.open();
            }

            // update atc buttons
            if(atcButtons){
              atcButtons.forEach((atcButton)=>{
                atcButton.removeAttribute('aria-disabled');
                atcButton.classList.remove('loading');
                atcButton.querySelector('.loading-overlay__spinner').classList.add('hidden');
              });
            }

            // get the active quick add to bag
            const activeQuickAddToBag = this.querySelector('quick-add-to-bag.active');
            // exit if no active quick add to bag
            if(!activeQuickAddToBag) return;
            // close the active quick add to bag
            activeQuickAddToBag.close();

          })
          .catch((error) => {
            console.error(error, this);

          });

      } catch(error){
        console.error(error, this);

      }
    }

    /**
     * Updates all product links with a new URL
     * @param {string} url - The new URL to set
     */
    setProductLinks(url){
      // early exit conditions
      if(!url) return;
      // exit if no product links
      const productLinks = this.querySelectorAll('a');
      if(!productLinks) return;
      // set the product links
      productLinks.forEach((productLink)=>{
        productLink.href = url;
      });
      // get the quickview link
      const quickviewLink = this.querySelector('.quick-add__submit');
      // set the product url
      if(quickviewLink) quickviewLink.dataset.productUrl = url;
    }

    /**
     * Updates the price element with new HTML
     * @param {string} html - The HTML to set for the price
     */
    setPriceElement(html){
      // early exit conditions
      if(!html) return;
      // exit if no price element
      if(!this.elements.priceElement) return;
      // set the price element
      this.elements.priceElement.replaceChildren();
      // set the price element
      this.elements.priceElement.appendChild(html);
    }

    /**
     * Gets the product data
     * @returns {Object} The product data
     */
    getProduct(){
      return this.product ?? {};
    }

    /**
     * Gets the selected swatch element
     * @returns {HTMLElement|null} The selected swatch element
     */
    getSelectedSwatchElement(){
      return this.selectedSwatchElement ?? null;
    }

    /**
     * Gets the selected swatch data
     * @returns {Object|null} The selected swatch data
     */
    getSelectedSwatchData(){
      return this.selectedSwatchData ?? {};
    }

    /**
     * Gets the HTML for the price range
     * @returns {string} The price range HTML
     */
    getPriceRangeHTML(){
      return this.product.price_range_html ?? null;
    }

    /**
     * Gets the variants for the product
     * @returns {Array} Array of variant objects
     */
    getVariants(){
      return this.product.variants ?? [];
    }

    /**
     * Gets the bundle products if this is a bundle product
     * @returns {Array} Array of bundle product objects
     */
    getBundleProducts(){
      return this.product.bundle_products ?? [];
    }

    /**
     * Gets the current variant
     * @returns {Object|null} The current variant
     */
    getVariant(){
      return this.variant ?? {};
    }

    /**
     * Checks if the product has color swatches
     * @returns {boolean} Whether the product has swatches
     */
    hasSwatches() {
      return this.product.swatches?.length > 0;
    }

    /**
     * Gets the swatch data for the product
     * @returns {Array} Array of swatch objects
     */
    getSwatchesData() {
      return this.product.swatches;
    }

    /**
     * Checks if this is a standard product (not complex or bundle)
     * @returns {boolean} Whether this is a standard product
     */
    isStandardProduct(){
      return this.product.is_standard_product ?? false;
    }

    /**
     * Checks if this is a parent product in a complex product
     * @returns {boolean} Whether this is a parent product
     */
    isParentProduct(){
      return this.product.is_parent_product ?? false;
    }

    /**
     * Checks if this is a bundle product
     * @returns {boolean} Whether this is a bundle product
     */
    isBundleProduct(){
      return this.product.is_bundle_product ?? false;
    }

    /**
     * Checks if this is a single variant product
     * @returns {boolean} Whether this is a single variant product
     */
    isSingleVariantProduct(){
      return this.product.is_single_variant_product ?? false;
    }

  }

  customElements.define('product-card', ProductCard);
}
